/**
 * The hero canvas: mounts the punchboard animation and owns its lifetime.
 *
 * `sheet.ts` has the timeline and the drawing; everything here is the bit that
 * touches the browser, and all of it is about not leaking:
 *
 *   - The ticker is stopped on unmount, and stopping cancels the frame already
 *     queued. React 19's StrictMode mounts twice in development, so a loop that
 *     survived unmount would double for the rest of the session.
 *   - A hidden tab stops the loop outright rather than throttling it. This is a
 *     PWA on a phone; a decorative loop running behind a locked screen is
 *     somebody's battery.
 *   - `prefers-reduced-motion` never starts a loop at all. It paints one
 *     composed frame — a part-punched sheet with a full table revealed — because
 *     "reduce motion" is a request to hold still, not to show nothing.
 *
 * The canvas is `aria-hidden` and carries no information that is not also said
 * in words beside it. Nothing here is a control.
 */

import { useEffect, useRef } from "react";
import type { ReactNode } from "react";

import {
  browserTicker,
  advance,
  composeStatic,
  createSheet,
  drawSheet,
  readPalette,
  type Palette,
  type SheetState,
} from "./sheet.ts";

/** How often the theme is re-read off the DOM. Cheap at this rate, live enough. */
const PALETTE_EVERY = 1000;

function reducedMotion(): boolean {
  return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function PunchSheet(): ReactNode {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    let palette: Palette = readPalette(canvas);
    let sincePalette = 0;
    let view = { width: 0, height: 0 };

    /**
     * Size the backing store to device pixels and the coordinate system back to
     * CSS pixels, so `sheet.ts` can measure in the units the layout is written
     * in and still be sharp on a phone.
     */
    const resize = (): void => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      view = { width: Math.max(1, rect.width), height: Math.max(1, rect.height) };
      canvas.width = Math.round(view.width * dpr);
      canvas.height = Math.round(view.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      palette = readPalette(canvas);
    };

    const still = reducedMotion();
    const state: SheetState = still ? composeStatic() : createSheet();

    resize();

    if (still) {
      // One frame, then nothing. Redrawn on resize and on a theme change only.
      const paint = (): void => {
        resize();
        drawSheet(ctx, state, view, palette);
      };
      paint();
      window.addEventListener("resize", paint);
      return () => window.removeEventListener("resize", paint);
    }

    const ticker = browserTicker((delta) => {
      sincePalette += delta;
      if (sincePalette >= PALETTE_EVERY) {
        sincePalette = 0;
        palette = readPalette(canvas);
      }
      advance(state, delta);
      drawSheet(ctx, state, view, palette);
    });

    const onVisibility = (): void => {
      if (document.visibilityState === "visible") ticker.start();
      else ticker.stop();
    };

    const onResize = (): void => {
      resize();
      drawSheet(ctx, state, view, palette);
    };

    if (document.visibilityState === "visible") ticker.start();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("resize", onResize);

    return () => {
      ticker.stop();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return <canvas ref={canvasRef} className="pb-door__canvas" aria-hidden="true" />;
}
